import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { CalendarIcon, Loader2, Upload, X, Plus, Trash2 } from 'lucide-react';
import { getPublicForm, getRegions, createSubmission, uploadFile, getGlobalDropdowns } from '@/lib/api';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DatePickerSimple } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('/api/uploads') || logoUrl.startsWith('/uploads')) {
    return `${BACKEND_URL}${logoUrl}`;
  }
  return logoUrl;
};

export default function PublicForm() {
  const { slug, departmentSlug, formSlug, shortcode } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [regions, setRegions] = useState([]);
  const [globalDropdowns, setGlobalDropdowns] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fieldValues, setFieldValues] = useState({});
  const [fileUploads, setFileUploads] = useState({});
  const [errors, setErrors] = useState({});
  
  // Bot protection state
  const [botChallenge, setBotChallenge] = useState(null);
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [challengeVerified, setChallengeVerified] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let formRes;
        
        // Determine how to fetch the form based on URL pattern
        if (shortcode) {
          // Shortlink: /f/{shortcode}
          const shortlinkRes = await axios.get(`${BACKEND_URL}/api/public/shortlink/${shortcode}`);
          formRes = shortlinkRes.data.form;
        } else if (departmentSlug && formSlug) {
          // Department + Form slug: /{department}/{form}
          formRes = await axios.get(`${BACKEND_URL}/api/public/form/${departmentSlug}/${formSlug}`);
          formRes = formRes.data;
        } else {
          // Legacy: /form/{slug}
          formRes = await getPublicForm(slug);
          formRes = formRes.data || formRes;
        }
        
        const form = formRes;
        setFormData(form);
        
        // Fetch regions
        const regionsRes = await getRegions();
        setRegions(regionsRes.data?.regions || regionsRes.regions || []);
        
        // Fetch global dropdowns if needed
        const dropdownIds = (form.fields || [])
          .filter(f => f.global_dropdown_id)
          .map(f => f.global_dropdown_id);
        
        if (dropdownIds.length > 0) {
          try {
            const dropdowns = await getGlobalDropdowns();
            const ddMap = {};
            dropdowns.forEach(dd => { ddMap[dd.id] = dd.values || []; });
            setGlobalDropdowns(ddMap);
          } catch (e) {
            console.error('Error loading dropdowns:', e);
          }
        }
        
        // Check if bot protection is needed
        if (form.bot_protection && form.bot_protection !== 'off') {
          fetchBotChallenge();
        }
        
        // Initialize field values
        const initialValues = {};
        (form.fields || []).forEach(field => {
          if (field.field_type === 'repeatable_group') {
            // Initialize with min_repeats instances
            const minRepeats = field.min_repeats || 1;
            initialValues[field.field_id] = Array(minRepeats).fill(null).map(() => {
              const groupValues = {};
              (field.group_fields || []).forEach(gf => {
                groupValues[gf.field_id] = '';
              });
              return groupValues;
            });
          } else if (field.field_type === 'checkbox') {
            initialValues[field.field_id] = [];
          } else {
            initialValues[field.field_id] = '';
          }
        });
        setFieldValues(initialValues);
      } catch (error) {
        toast.error('Formular konnte nicht geladen werden');
        console.error('Error loading form:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, departmentSlug, formSlug, shortcode]);

  const fetchBotChallenge = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/bot-challenge`);
      setBotChallenge(res.data);
    } catch (e) {
      console.error('Error fetching bot challenge:', e);
    }
  };

  const verifyBotChallenge = async () => {
    if (!botChallenge || !challengeAnswer) return false;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/bot-challenge/verify`, {
        challenge_id: botChallenge.challenge_id,
        answer: challengeAnswer
      });
      if (res.data.verified) {
        setChallengeVerified(true);
        return true;
      } else {
        toast.error('Falsche Antwort, bitte versuchen Sie es erneut');
        fetchBotChallenge();
        setChallengeAnswer('');
        return false;
      }
    } catch (e) {
      toast.error('Fehler bei der Verifizierung');
      return false;
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const handleFileChange = async (fieldId, file) => {
    if (!file) {
      setFileUploads(prev => ({ ...prev, [fieldId]: null }));
      setFieldValues(prev => ({ ...prev, [fieldId]: '' }));
      return;
    }

    // Validate file
    const field = formData.fields.find(f => f.field_id === fieldId);
    if (field) {
      const maxSize = (field.max_file_size_mb || 10) * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`Datei zu groß. Max: ${field.max_file_size_mb || 10} MB`);
        return;
      }

      const allowedTypes = field.allowed_file_types || [];
      if (allowedTypes.length > 0) {
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowedTypes.some(t => t.toLowerCase() === ext)) {
          toast.error(`Dateityp nicht erlaubt. Erlaubt: ${allowedTypes.join(', ')}`);
          return;
        }
      }
    }

    setFileUploads(prev => ({ ...prev, [fieldId]: file }));
    setFieldValues(prev => ({ ...prev, [fieldId]: file.name }));
  };

  const handleRepeatableGroupChange = (fieldId, index, subFieldId, value) => {
    setFieldValues(prev => {
      const groups = [...(prev[fieldId] || [])];
      if (!groups[index]) groups[index] = {};
      groups[index][subFieldId] = value;
      return { ...prev, [fieldId]: groups };
    });
  };

  const addRepeatableGroup = (fieldId) => {
    const field = formData.fields.find(f => f.field_id === fieldId);
    const maxRepeats = field?.max_repeats || 10;
    const currentCount = (fieldValues[fieldId] || []).length;
    
    if (currentCount >= maxRepeats) {
      toast.error(`Maximum ${maxRepeats} Einträge erlaubt`);
      return;
    }

    const newGroup = {};
    (field?.group_fields || []).forEach(gf => {
      newGroup[gf.field_id] = '';
    });

    setFieldValues(prev => ({
      ...prev,
      [fieldId]: [...(prev[fieldId] || []), newGroup]
    }));
  };

  const removeRepeatableGroup = (fieldId, index) => {
    const field = formData.fields.find(f => f.field_id === fieldId);
    const minRepeats = field?.min_repeats || 1;
    const currentCount = (fieldValues[fieldId] || []).length;
    
    if (currentCount <= minRepeats) {
      toast.error(`Minimum ${minRepeats} Eintrag erforderlich`);
      return;
    }

    setFieldValues(prev => ({
      ...prev,
      [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index)
    }));
  };

  const checkVisibility = (field) => {
    if (!field.conditional_visibility) return true;
    const { field_id, value } = field.conditional_visibility;
    const currentValue = fieldValues[field_id];
    
    if (Array.isArray(currentValue)) {
      return currentValue.includes(value);
    }
    return currentValue === value;
  };

  const validateForm = () => {
    const newErrors = {};
    const fields = formData?.fields || [];
    
    fields.forEach(field => {
      if (!checkVisibility(field)) return;
      
      if (field.required) {
        const value = fieldValues[field.field_id];
        
        if (field.field_type === 'repeatable_group') {
          const groups = value || [];
          if (groups.length === 0) {
            newErrors[field.field_id] = `${field.label} ist erforderlich`;
          }
        } else if (field.field_type === 'composite') {
          // Validate composite fields - check if any required sub-field is empty
          const compositeValues = value || {};
          const compositeFields = field.composite_fields || [];
          const hasRequiredEmpty = compositeFields.some(cf => 
            cf.required && (!compositeValues[cf.field_id] || !String(compositeValues[cf.field_id]).trim())
          );
          if (hasRequiredEmpty || Object.keys(compositeValues).length === 0) {
            newErrors[field.field_id] = `${field.label} ist erforderlich`;
          }
        } else if (field.field_type === 'checkbox') {
          if (!value || value.length === 0) {
            newErrors[field.field_id] = `${field.label} ist erforderlich`;
          }
        } else if (field.field_type === 'file') {
          if (!fileUploads[field.field_id] && !value) {
            newErrors[field.field_id] = `${field.label} ist erforderlich`;
          }
        } else if (!value || (typeof value === 'string' && !value.trim())) {
          newErrors[field.field_id] = `${field.label} ist erforderlich`;
        }
      }

      // Additional validations
      const value = fieldValues[field.field_id];
      if (value && typeof value === 'string') {
        if (field.min_length && value.length < field.min_length) {
          newErrors[field.field_id] = `Mindestens ${field.min_length} Zeichen`;
        }
        if (field.max_length && value.length > field.max_length) {
          newErrors[field.field_id] = `Maximal ${field.max_length} Zeichen`;
        }
        if (field.pattern) {
          try {
            if (!new RegExp(field.pattern).test(value)) {
              newErrors[field.field_id] = `Ungültiges Format`;
            }
          } catch (e) {
            // Invalid regex pattern, skip validation
          }
        }
      }
      
      if (field.field_type === 'number' && value) {
        const numVal = parseFloat(value);
        if (field.min_value !== null && numVal < field.min_value) {
          newErrors[field.field_id] = `Minimum: ${field.min_value}`;
        }
        if (field.max_value !== null && numVal > field.max_value) {
          newErrors[field.field_id] = `Maximum: ${field.max_value}`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Verify bot challenge if required and not yet verified
    if (botChallenge && !challengeVerified) {
      if (!challengeAnswer.trim()) {
        toast.error('Bitte beantworten Sie die Sicherheitsfrage');
        return;
      }
      
      const verified = await verifyBotChallenge();
      if (!verified) {
        return; // verifyBotChallenge already shows error toast
      }
    }

    setSubmitting(true);
    try {
      // Upload files first
      const submissionData = { ...fieldValues };
      
      for (const [fieldId, file] of Object.entries(fileUploads)) {
        if (file) {
          try {
            const formDataObj = new FormData();
            formDataObj.append('file', file);
            
            const response = await axios.post(`${BACKEND_URL}/api/uploads/file`, formDataObj, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            submissionData[fieldId] = {
              filename: response.data.filename,
              original_name: file.name,
              url: response.data.url
            };
          } catch (uploadError) {
            console.error('File upload error:', uploadError);
            toast.error(`Datei-Upload fehlgeschlagen: ${file.name}`);
            setSubmitting(false);
            return;
          }
        }
      }

      // Format date fields
      (formData?.fields || []).forEach(field => {
        if (field.field_type === 'date' && submissionData[field.field_id]) {
          const dateValue = submissionData[field.field_id];
          if (dateValue instanceof Date) {
            submissionData[field.field_id] = format(dateValue, 'yyyy-MM-dd');
          }
        }
      });

      await createSubmission(formData.id, submissionData);
      
      // Navigate to success page based on URL pattern
      if (departmentSlug && formSlug) {
        navigate(`/${departmentSlug}/${formSlug}/success`);
      } else if (shortcode) {
        navigate(`/f/${shortcode}/success`);
      } else {
        navigate(`/form/${slug}/success`);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Ein Fehler ist aufgetreten';
      toast.error(message);
      console.error('Submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldOptions = (field) => {
    if (field.use_regions) return regions;
    if (field.global_dropdown_id && globalDropdowns[field.global_dropdown_id]) {
      return globalDropdowns[field.global_dropdown_id];
    }
    return field.options || [];
  };

  const renderField = (field) => {
    if (!checkVisibility(field)) return null;

    const value = fieldValues[field.field_id];
    const error = errors[field.field_id];
    const options = getFieldOptions(field);

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
        return (
          <div key={field.field_id} className="space-y-2">
            <Label className="text-slate-700 font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type={field.field_type === 'number' ? 'number' : field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
              placeholder={field.placeholder}
              className={cn("bg-white", error && "border-red-500")}
              data-testid={`field-${field.field_id}`}
              min={field.min_value}
              max={field.max_value}
              minLength={field.min_length}
              maxLength={field.max_length}
            />
            {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.field_id} className="space-y-2">
            <Label className="text-slate-700 font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={value || ''}
              onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
              placeholder={field.placeholder}
              className={cn("bg-white min-h-[100px]", error && "border-red-500")}
              data-testid={`field-${field.field_id}`}
              minLength={field.min_length}
              maxLength={field.max_length}
            />
            {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={field.field_id} className="space-y-2">
            <Label className="text-slate-700 font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full pl-3 text-left font-normal bg-white justify-start",
                    !value && "text-muted-foreground",
                    error && "border-red-500"
                  )}
                  data-testid={`field-${field.field_id}`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {value ? (
                    format(value instanceof Date ? value : new Date(value), "dd.MM.yyyy")
                  ) : (
                    <span>{field.placeholder || "Datum wählen"}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <DatePickerSimple
                  // selected={value instanceof Date ? value : (value ? new Date(value) : undefined)}
                  //onSelect={(date) => handleFieldChange(field.field_id, date)}
                  // disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                  //disabled={(date) =>
 		  //date < new Date(new Date().setHours(0, 0, 0, 0))
		//	}
		 // fromYear={1920}
                  //toYear={new Date().getFullYear()}
               // />
		  selected={value instanceof Date ? value : (value ? new Date(value) : undefined)}
  		  onSelect={(date) => handleFieldChange(field.field_id, date)}
		  fromYear={1920}
  		  toYear={new Date().getFullYear() + 20}
		/>
              </PopoverContent>
            </Popover>
            {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'time':
        return (
          <div key={field.field_id} className="space-y-2">
            <Label className="text-slate-700 font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="time"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.field_id, e.target.value)}
              placeholder={field.placeholder}
              className={cn("bg-white", error && "border-red-500")}
              data-testid={`field-${field.field_id}`}
            />
            {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.field_id} className="space-y-2">
            <Label className="text-slate-700 font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select value={value || ''} onValueChange={(v) => handleFieldChange(field.field_id, v)}>
              <SelectTrigger className={cn("bg-white", error && "border-red-500")} data-testid={`field-${field.field_id}`}>
                <SelectValue placeholder={field.placeholder || "Bitte wählen..."} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option, i) => (
                  <SelectItem key={i} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'radio':
        return (
          <div key={field.field_id} className="space-y-2">
            <Label className="text-slate-700 font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2" data-testid={`field-${field.field_id}`}>
              {options.map((option, i) => (
                <label key={i} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={field.field_id}
                    value={option}
                    checked={value === option}
                    onChange={() => handleFieldChange(field.field_id, option)}
                    className="w-4 h-4"
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
            {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.field_id} className="space-y-2">
            <Label className="text-slate-700 font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2" data-testid={`field-${field.field_id}`}>
              {options.map((option, i) => (
                <label key={i} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    value={option}
                    checked={(value || []).includes(option)}
                    onChange={(e) => {
                      const current = value || [];
                      const newValue = e.target.checked
                        ? [...current, option]
                        : current.filter(v => v !== option);
                      handleFieldChange(field.field_id, newValue);
                    }}
                    className="w-4 h-4 rounded"
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
            {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'file':
        return (
          <div key={field.field_id} className="space-y-2">
            <Label className="text-slate-700 font-medium">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className={cn("border-2 border-dashed rounded-lg p-4 transition-colors", error ? "border-red-500" : "border-gray-300 hover:border-blue-400")}>
              {fileUploads[field.field_id] ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-green-600" />
                    <span className="text-sm">{fileUploads[field.field_id].name}</span>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => handleFileChange(field.field_id, null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center cursor-pointer">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Datei auswählen</span>
                  {field.allowed_file_types && (
                    <span className="text-xs text-gray-400 mt-1">
                      Erlaubt: {field.allowed_file_types.join(', ')}
                    </span>
                  )}
                  <input
                    type="file"
                    className="hidden"
                    accept={field.allowed_file_types?.join(',')}
                    onChange={(e) => handleFileChange(field.field_id, e.target.files?.[0])}
                    data-testid={`field-${field.field_id}`}
                  />
                </label>
              )}
            </div>
            {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'heading':
        return (
          <div key={field.field_id} className="pt-4">
            <h3 className="text-lg font-semibold text-slate-800">{field.label}</h3>
            {field.help_text && <p className="text-sm text-gray-500">{field.help_text}</p>}
          </div>
        );

      case 'info':
        return (
          <div key={field.field_id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">{field.label}</p>
            {field.help_text && <p className="text-xs text-blue-600 mt-1">{field.help_text}</p>}
          </div>
        );

      case 'repeatable_group':
        const groups = value || [];
        
        // Helper function to render group field based on type
        const renderGroupField = (gf, groupIndex, groupValues) => {
          const gfValue = groupValues[gf.field_id] || '';
          const handleChange = (newValue) => handleRepeatableGroupChange(field.field_id, groupIndex, gf.field_id, newValue);
          
          switch (gf.field_type) {
            case 'text':
            case 'email':
            case 'phone':
              return (
                <Input
                  type={gf.field_type === 'email' ? 'email' : gf.field_type === 'phone' ? 'tel' : 'text'}
                  value={gfValue}
                  onChange={(e) => handleChange(e.target.value)}
                  placeholder={gf.placeholder}
                  className="bg-white mt-1"
                  minLength={gf.min_length}
                  maxLength={gf.max_length}
                />
              );
            
            case 'textarea':
              return (
                <Textarea
                  value={gfValue}
                  onChange={(e) => handleChange(e.target.value)}
                  placeholder={gf.placeholder}
                  className="bg-white mt-1"
                  rows={3}
                />
              );
            
            case 'number':
              return (
                <Input
                  type="number"
                  value={gfValue}
                  onChange={(e) => handleChange(e.target.value)}
                  placeholder={gf.placeholder}
                  className="bg-white mt-1"
                  min={gf.min_value}
                  max={gf.max_value}
                />
              );
            
            case 'date':
              return (
                <Input
                  type="date"
                  value={gfValue}
                  onChange={(e) => handleChange(e.target.value)}
                  className="bg-white mt-1"
                />
              );
            
            case 'time':
              return (
                <Input
                  type="time"
                  value={gfValue}
                  onChange={(e) => handleChange(e.target.value)}
                  className="bg-white mt-1"
                />
              );
            
            case 'select':
              return (
                <Select value={gfValue || ''} onValueChange={handleChange}>
                  <SelectTrigger className="bg-white mt-1">
                    <SelectValue placeholder={gf.placeholder || 'Bitte auswählen...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(gf.options || []).map((opt, idx) => (
                      <SelectItem key={idx} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            
            case 'radio':
              return (
                <RadioGroup value={gfValue} onValueChange={handleChange} className="mt-2 space-y-2">
                  {(gf.options || []).map((opt, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <RadioGroupItem value={opt} id={`${gf.field_id}_${groupIndex}_${idx}`} />
                      <Label htmlFor={`${gf.field_id}_${groupIndex}_${idx}`} className="font-normal text-sm">{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              );
            
            case 'checkbox':
              const checkboxValues = Array.isArray(gfValue) ? gfValue : [];
              return (
                <div className="mt-2 space-y-2">
                  {(gf.options || []).map((opt, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${gf.field_id}_${groupIndex}_${idx}`}
                        checked={checkboxValues.includes(opt)}
                        onCheckedChange={(checked) => {
                          const newValues = checked 
                            ? [...checkboxValues, opt]
                            : checkboxValues.filter(v => v !== opt);
                          handleChange(newValues);
                        }}
                      />
                      <Label htmlFor={`${gf.field_id}_${groupIndex}_${idx}`} className="font-normal text-sm">{opt}</Label>
                    </div>
                  ))}
                </div>
              );
            
            default:
              return (
                <Input
                  type="text"
                  value={gfValue}
                  onChange={(e) => handleChange(e.target.value)}
                  placeholder={gf.placeholder}
                  className="bg-white mt-1"
                />
              );
          }
        };
        
        return (
          <div key={field.field_id} className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-slate-700 font-medium">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addRepeatableGroup(field.field_id)}
                disabled={groups.length >= (field.max_repeats || 10)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Hinzufügen
              </Button>
            </div>
            {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            
            <div className="space-y-3">
              {groups.map((groupValues, groupIndex) => (
                <div key={groupIndex} className="border rounded-lg p-4 bg-gray-50 relative">
                  <div className="absolute top-2 right-2 flex items-center gap-1">
                    <span className="text-xs text-gray-400">#{groupIndex + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => removeRepeatableGroup(field.field_id, groupIndex)}
                      disabled={groups.length <= (field.min_repeats || 1)}
                    >
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                  
                  <div className="space-y-3 pr-16">
                    {(field.group_fields || []).map((gf) => (
                      <div key={gf.field_id}>
                        <Label className="text-sm">
                          {gf.label}
                          {gf.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {renderGroupField(gf, groupIndex, groupValues)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      case 'composite':
        const compositeValues = fieldValues[field.field_id] || {};
        const compositeFields = field.composite_fields || [];
        
        return (
          <div key={field.field_id} className="space-y-3" data-testid={`composite-${field.field_id}`}>
            <Label className="text-slate-700 font-medium block">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            {field.help_text && <p className="text-xs text-gray-500 -mt-2">{field.help_text}</p>}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 border border-emerald-200 rounded-lg bg-emerald-50/30">
              {compositeFields.map((cf) => {
                const cfValue = compositeValues[cf.field_id] || '';
                
                const handleCompositeChange = (newVal) => {
                  setFieldValues(prev => ({
                    ...prev,
                    [field.field_id]: {
                      ...(prev[field.field_id] || {}),
                      [cf.field_id]: newVal
                    }
                  }));
                };
                
                return (
                  <div key={cf.field_id}>
                    <Label className="text-sm text-slate-600">
                      {cf.label}
                      {cf.required && <span className="text-red-500 ml-0.5">*</span>}
                    </Label>
                    
                    {cf.field_type === 'text' && (
                      <Input
                        type="text"
                        value={cfValue}
                        onChange={(e) => handleCompositeChange(e.target.value)}
                        placeholder={cf.placeholder}
                        className="bg-white mt-1"
                      />
                    )}
                    
                    {cf.field_type === 'date' && (
                      <Input
                        type="date"
                        value={cfValue}
                        onChange={(e) => handleCompositeChange(e.target.value)}
                        className="bg-white mt-1"
                      />
                    )}
                    
                    {cf.field_type === 'select' && (
                      <Select value={cfValue || ''} onValueChange={handleCompositeChange}>
                        <SelectTrigger className="bg-white mt-1">
                          <SelectValue placeholder={cf.placeholder || 'Bitte auswählen...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {(cf.options || []).map((opt, idx) => (
                            <SelectItem key={idx} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="public-form-bg min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="public-form-bg min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Formular nicht gefunden</h1>
          <p className="text-slate-600 mt-2">Bu form mevcut değil veya aktif değil.</p>
        </div>
      </div>
    );
  }

  const sortedFields = [...(formData.fields || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const visibleFields = sortedFields.filter(checkVisibility);
  const requiredFields = visibleFields.filter(f => f.required && !['heading', 'info'].includes(f.field_type));
  const optionalFields = visibleFields.filter(f => !f.required && !['heading', 'info'].includes(f.field_type));
  const infoFields = visibleFields.filter(f => ['heading', 'info'].includes(f.field_type));

  return (
    <div className="public-form-bg min-h-screen py-8 px-4 sm:py-12">
      <div className="max-w-2xl mx-auto">
        {/* Header with Logo */}
        <div className="text-center mb-8">
          {formData.logo_url && (
            <div className="mb-6">
              <img 
                src={getLogoUrl(formData.logo_url)} 
                alt="Logo" 
                className="h-24 sm:h-28 mx-auto object-contain"
                data-testid="form-logo"
              />
            </div>
          )}
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900" data-testid="form-title">
            {formData.title || formData.name}
          </h1>
          {formData.description && (
            <p className="mt-3 text-slate-600">{formData.description}</p>
          )}
        </div>

        {/* Form Card */}
        <div className="form-container p-8 md:p-12 animate-fade-in">
          <form onSubmit={onSubmit} className="space-y-6">
            {/* Honeypot fields (invisible to users, traps bots) */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={fieldValues._hp_website || ''}
                onChange={(e) => setFieldValues(prev => ({ ...prev, _hp_website: e.target.value }))}
              />
              <input
                type="text"
                name="fax_number"
                tabIndex={-1}
                autoComplete="off"
                value={fieldValues._hp_fax || ''}
                onChange={(e) => setFieldValues(prev => ({ ...prev, _hp_fax: e.target.value }))}
              />
            </div>

            {/* All fields in order */}
            {sortedFields.map(field => renderField(field))}

            {/* Bot Protection Challenge */}
            {botChallenge && !challengeVerified && (
              <div className="border-2 border-amber-200 bg-amber-50 rounded-lg p-6 space-y-4" data-testid="bot-challenge-section">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-amber-500 flex items-center justify-center">
                    <span className="text-white text-lg">🔒</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">Sicherheitsprüfung</h3>
                    <p className="text-sm text-amber-600">Bitte beantworten Sie die folgende Frage</p>
                  </div>
                </div>
                
                <div className="bg-white rounded-md p-4 border border-amber-200">
                  {botChallenge.type === 'math' ? (
                    <Label className="text-slate-700 font-medium mb-2 block" data-testid="bot-challenge-question">
                      Was ist <span className="text-lg font-bold text-amber-700">{botChallenge.question}</span> ?
                    </Label>
                  ) : (
                    <Label className="text-slate-700 font-medium mb-2 block" data-testid="bot-challenge-question">
                      Welche Zahl kommt als nächstes? <span className="text-lg font-bold text-amber-700">{botChallenge.question}, ?</span>
                    </Label>
                  )}
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={challengeAnswer}
                    onChange={(e) => setChallengeAnswer(e.target.value)}
                    placeholder="Ihre Antwort eingeben..."
                    className="mt-2"
                    data-testid="bot-challenge-input"
                  />
                </div>
              </div>
            )}

            {/* Challenge verified indicator */}
            {challengeVerified && (
              <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-lg" data-testid="bot-challenge-verified">
                <span className="text-lg">✓</span>
                <span className="text-sm font-medium">Sicherheitsprüfung bestanden</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <Button 
                type="submit" 
                className="w-full h-12 text-base font-medium"
                disabled={submitting || (botChallenge && !challengeVerified && !challengeAnswer)}
                data-testid="submit-form-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  'Formular absenden'
                )}
              </Button>
            </div>

            {requiredFields.length > 0 && (
              <p className="text-xs text-slate-500 text-center mt-4">
                <span className="text-red-500">*</span> Pflichtfelder
              </p>
            )}
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-8">
          Ihre Daten werden sicher gespeichert.
        </p>
      </div>
    </div>
  );
}
